import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StatisticsService } from './statistics.service';
import { forkJoin } from 'rxjs';
import { Chart, registerables, TimeUnit } from 'chart.js';
import { MatTabChangeEvent } from '@angular/material/tabs';
import 'chartjs-adapter-moment';

Chart.register(...registerables);

// Custom plugin to draw vertical marker lines (restarts, config changes)
const markerLinesPlugin = {
  id: 'markerLines',
  afterDraw(chart: any) {
    const markers = chart.options?.plugins?.markerLines?.markers;
    if (!markers || markers.length === 0) return;
    const { ctx, chartArea, scales } = chart;
    if (!scales?.x) return;

    for (const marker of markers) {
      const x = scales.x.getPixelForValue(new Date(marker.startTime).getTime());
      if (x >= chartArea.left && x <= chartArea.right) {
        ctx.save();
        ctx.strokeStyle = marker.type === 'restart' ? '#e53935' : '#8e24aa';
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.fillStyle = ctx.strokeStyle;
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(
          marker.type === 'restart' ? 'RESTART' : 'CONFIG',
          x + 3,
          chartArea.top + 12,
        );
        ctx.restore();
      }
    }
  },
};

Chart.register(markerLinesPlugin);

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
})
export class StatisticsComponent implements OnInit, OnDestroy {
  private static readonly STORAGE_KEY = 'statistics_range';
  selectedRange = localStorage.getItem(StatisticsComponent.STORAGE_KEY) || '24h';
  recentEvents: any[] = [];
  queueCounts: { needsReload: number; needsInpostReload: number } = {
    needsReload: 0,
    needsInpostReload: 0,
  };
  allegroRate = 0;
  inpostRate = 0;
  allegroEta = '';
  inpostEta = '';
  criticalErrors: any[] = [];
  inpostRateLimit: { velocity: number | null, limit: number | null; remaining: number | null; reset: number | null; lastUpdated: string | null } | null = null;
  criticalErrorColumns = [
    'createdAt',
    'provider',
    'TK',
    'offerId',
    'errorMessage',
    'unpublished',
    'resolved',
    'actions',
  ];
  private queueInterval: any;
  private static readonly QUEUE_POLL_SECONDS = 30;
  queueCountdown = StatisticsComponent.QUEUE_POLL_SECONDS;
  queueLoading = false;
  displayedColumns = [
    'startTime',
    'type',
    'jobName',
    'provider',
    'status',
    'durationMs',
    'errorMessage',
  ];

  selectedTabIndex = 0;
  private charts: Chart[] = [];
  private lastData: { metrics: any[]; markers: any[]; aggregated: any[] } | null = null;

  constructor(
    private statisticsService: StatisticsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.loadQueues();
    this.loadCriticalErrors();
    // Tick every second to drive the countdown; refresh when it reaches 0.
    this.queueInterval = setInterval(() => {
      this.queueCountdown--;
      if (this.queueCountdown <= 0) {
        this.loadQueues();
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    if (this.queueInterval) {
      clearInterval(this.queueInterval);
    }
  }

  onRangeChange(event: any): void {
    this.selectedRange = event.value;
    localStorage.setItem(StatisticsComponent.STORAGE_KEY, this.selectedRange);
    this.loadData();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    // Rebuild chart for newly visible tab after Angular renders the canvas
    if (this.lastData) {
      setTimeout(() => {
        this.destroyCharts();
        this.buildChartsForTab(this.selectedTabIndex);
      }, 50);
    }
  }

  formatDuration(ms: number): string {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }

  private loadQueues(): void {
    // Skip if a previous (slow) request is still in flight to avoid pile-up.
    if (this.queueLoading) {
      return;
    }
    this.queueLoading = true;
    this.queueCountdown = StatisticsComponent.QUEUE_POLL_SECONDS;
    this.statisticsService.getQueues().subscribe((data) => {
      this.queueCounts = data;
      this.allegroEta = this.computeEta(this.queueCounts.needsReload, this.allegroRate);
      this.inpostEta = this.computeEta(this.queueCounts.needsInpostReload, this.inpostRate);
      this.queueLoading = false;
      // Reset again on completion so the countdown reflects the next full window.
      this.queueCountdown = StatisticsComponent.QUEUE_POLL_SECONDS;
    });
    this.statisticsService.getInpostRateLimit().subscribe((data) => {
      this.inpostRateLimit = data;
    });
  }

  private loadCriticalErrors(): void {
    this.statisticsService.getCriticalErrors(100).subscribe((data) => {
      this.criticalErrors = data;
    });
  }

  resolveCriticalError(id: string): void {
    this.statisticsService.resolveCriticalError(id).subscribe(() => {
      this.loadCriticalErrors();
    });
  }

  private loadData(): void {
    const { from, to } = this.getDateRange(this.selectedRange);

    forkJoin([
      this.statisticsService.getAggregated(from, to),
      this.statisticsService.getMarkers(from, to),
      this.statisticsService.getRecent(100),
      this.statisticsService.getMetrics(from, to),
    ]).subscribe(([aggregated, markers, recent, metrics]) => {
      this.recentEvents = recent;
      this.lastData = { metrics, markers, aggregated };
      this.updateRates(metrics);

      setTimeout(() => {
        this.destroyCharts();
        this.buildChartsForTab(this.selectedTabIndex);
      }, 100);
    });
  }

  private getDateRange(range: string): { from: Date; to: Date } {
    const to = new Date();
    const from = new Date();
    switch (range) {
      case '1h':
        from.setHours(from.getHours() - 1);
        break;
      case '6h':
        from.setHours(from.getHours() - 6);
        break;
      case '24h':
        from.setDate(from.getDate() - 1);
        break;
      case '7d':
        from.setDate(from.getDate() - 7);
        break;
      case '30d':
        from.setDate(from.getDate() - 30);
        break;
    }
    return { from, to };
  }

  private calcRate(points: { x: any; y: number }[]): { x: any; y: number }[] {
    const rate: { x: any; y: number }[] = [];
    for (let i = 1; i < points.length; i++) {
      const dt = (new Date(points[i].x).getTime() - new Date(points[i - 1].x).getTime()) / 60000; // minutes
      if (dt <= 0) continue;
      const diff = points[i - 1].y - points[i].y; // positive = draining
      rate.push({ x: points[i].x, y: Math.round((diff / dt) * 10) / 10 });
    }
    return rate;
  }

  private updateRates(metrics: any[]): void {
    const allegro = metrics
      .filter((m) => m.provider === 'allegro' && m.allegroQueueSize != null)
      .map((m) => ({ x: m.bucket, y: m.allegroQueueSize }));
    const inpost = metrics
      .filter((m) => m.provider === 'inpost' && m.inpostQueueSize != null)
      .map((m) => ({ x: m.bucket, y: m.inpostQueueSize }));
    const allegroRateData = this.calcRate(allegro);
    const inpostRateData = this.calcRate(inpost);
    this.allegroRate = allegroRateData.length > 0 ? allegroRateData[allegroRateData.length - 1].y : 0;
    this.inpostRate = inpostRateData.length > 0 ? inpostRateData[inpostRateData.length - 1].y : 0;
    this.allegroEta = this.computeEta(this.queueCounts.needsReload, this.allegroRate);
    this.inpostEta = this.computeEta(this.queueCounts.needsInpostReload, this.inpostRate);
  }

  private computeEta(queueSize: number, rate: number): string {
    if (queueSize <= 0 || rate <= 0) return '';
    const totalMinutes = Math.ceil(queueSize / rate);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `~${hours}h ${minutes}min`;
    }
    return `~${minutes}min`;
  }

  private buildQueueChart(metrics: any[], markers: any[]): void {
    const canvas = document.getElementById('queueChart') as HTMLCanvasElement;
    if (!canvas) return;

    const allegro = metrics
      .filter((m) => m.provider === 'allegro' && m.allegroQueueSize != null)
      .map((m) => ({ x: m.bucket, y: m.allegroQueueSize }));

    const inpost = metrics
      .filter((m) => m.provider === 'inpost' && m.inpostQueueSize != null)
      .map((m) => ({ x: m.bucket, y: m.inpostQueueSize }));

    const allegroRateData = this.calcRate(allegro);
    const inpostRateData = this.calcRate(inpost);

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [
          {
            label: 'Allegro kolejka',
            data: allegro,
            borderColor: '#ff9800',
            backgroundColor: 'rgba(255,152,0,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            yAxisID: 'y',
          },
          {
            label: 'InPost kolejka',
            data: inpost,
            borderColor: '#4caf50',
            backgroundColor: 'rgba(76,175,80,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            yAxisID: 'y',
          },
          {
            label: 'Allegro tempo (szt/min)',
            data: allegroRateData,
            borderColor: '#e65100',
            borderDash: [5, 3],
            tension: 0.3,
            pointRadius: 2,
            yAxisID: 'yRate',
          },
          {
            label: 'InPost tempo (szt/min)',
            data: inpostRateData,
            borderColor: '#1b5e20',
            borderDash: [5, 3],
            tension: 0.3,
            pointRadius: 2,
            yAxisID: 'yRate',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            title: { display: true, text: 'Czas' },
          },
          y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Rozmiar kolejki' },
          },
          yRate: {
            type: 'linear',
            position: 'right',
            title: { display: true, text: 'Tempo (szt/min)' },
            grid: { drawOnChartArea: false },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildChartsForTab(tabIndex: number): void {
    if (!this.lastData) return;
    const { metrics, markers, aggregated } = this.lastData;
    switch (tabIndex) {
      case 0: this.buildQueueChart(metrics, markers); break;
      case 1: this.buildAllegroMetricsChart(metrics, markers); break;
      case 2: this.buildInpostMetricsChart(metrics, markers); break;
      case 3: this.buildIntercarsApiChart(metrics, markers); break;
      case 4: this.buildIntercarsCsvChart(metrics, markers); break;
      case 5: this.buildProcessingChart(aggregated, markers); break;
      case 6: this.buildSuccessFailChart(aggregated, markers); break;
      case 7: this.buildDurationChart(aggregated, markers); break;
      // Tab 8 = critical errors table, no chart
      // Tab 9 = recent events table, no chart
    }
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => c.destroy());
    this.charts = [];
  }

  private buildAllegroMetricsChart(metrics: any[], markers: any[]): void {
    const canvas = document.getElementById('allegroMetricsChart') as HTMLCanvasElement;
    if (!canvas) return;

    const allegro = metrics.filter((m) => m.provider === 'allegro');

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: allegro.map((d) => d.bucket),
        datasets: [
          {
            label: 'Utworzone',
            data: allegro.map((d) => d.auctionsCreated || 0),
            backgroundColor: '#4caf50',
          },
          {
            label: 'Zaktualizowane',
            data: allegro.map((d) => d.auctionsUpdated || 0),
            backgroundColor: '#2196f3',
          },
          {
            label: 'Bledy',
            data: allegro.map((d) => d.auctionsFailed || 0),
            backgroundColor: '#f44336',
          },
          {
            label: 'Pominiete',
            data: allegro.map((d) => d.auctionsSkipped || 0),
            backgroundColor: '#9e9e9e',
          },
          {
            label: 'Bledy aktualizacji',
            data: allegro.map((d) => d.auctionsUpdateErrors || 0),
            backgroundColor: '#d32f2f',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            stacked: true,
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: { display: true, text: 'Aukcje' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildInpostMetricsChart(metrics: any[], markers: any[]): void {
    const canvas = document.getElementById('inpostMetricsChart') as HTMLCanvasElement;
    if (!canvas) return;

    const inpost = metrics.filter((m) => m.provider === 'inpost');

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: inpost.map((d) => d.bucket),
        datasets: [
          {
            label: 'Utworzone',
            data: inpost.map((d) => d.offersCreated || 0),
            backgroundColor: '#4caf50',
          },
          {
            label: 'Zaktualizowane',
            data: inpost.map((d) => d.offersUpdated || 0),
            backgroundColor: '#2196f3',
          },
          {
            label: 'Bledy',
            data: inpost.map((d) => d.offersFailed || 0),
            backgroundColor: '#f44336',
          },
          {
            label: 'Pominiete',
            data: inpost.map((d) => d.offersSkipped || 0),
            backgroundColor: '#9e9e9e',
          },
          {
            label: 'Bledy aktualizacji',
            data: inpost.map((d) => d.offersUpdateErrors || 0),
            backgroundColor: '#d32f2f',
          },
          {
            label: 'Kat. zsync.',
            data: inpost.map((d) => d.categorySynced || 0),
            backgroundColor: '#7c4dff',
          },
          {
            label: 'Kat. naprawione',
            data: inpost.map((d) => d.categoryFixed || 0),
            backgroundColor: '#00c853',
          },
          {
            label: 'Kat. bledy',
            data: inpost.map((d) => d.categoryFailed || 0),
            backgroundColor: '#ff6d00',
          },
          {
            label: 'Kat. brak ref.',
            data: inpost.map((d) => d.categoryNoRef || 0),
            backgroundColor: '#78909c',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            stacked: true,
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: { display: true, text: 'Oferty' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildIntercarsApiChart(metrics: any[], markers: any[]): void {
    const canvas = document.getElementById('intercarsApiChart') as HTMLCanvasElement;
    if (!canvas) return;

    const intercars = metrics.filter((m) => m.provider === 'intercars');

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: intercars.map((d) => d.bucket),
        datasets: [
          {
            label: 'Sprawdzone (API)',
            data: intercars.map((d) => d.stockChecked || 0),
            backgroundColor: '#2196f3',
          },
          {
            label: 'Zmienione',
            data: intercars.map((d) => d.stockChanged || 0),
            backgroundColor: '#ff9800',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            stacked: true,
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: { display: true, text: 'Produkty (API)' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildIntercarsCsvChart(metrics: any[], markers: any[]): void {
    const canvas = document.getElementById('intercarsCsvChart') as HTMLCanvasElement;
    if (!canvas) return;

    const intercars = metrics.filter((m) => m.provider === 'intercars');

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: intercars.map((d) => d.bucket),
        datasets: [
          {
            label: 'Nowe produkty',
            data: intercars.map((d) => d.productsInserted || 0),
            backgroundColor: '#4caf50',
          },
          {
            label: 'Zaktualizowane',
            data: intercars.map((d) => d.productsUpdated || 0),
            backgroundColor: '#2196f3',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            stacked: true,
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: { display: true, text: 'Produkty (CSV)' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildProcessingChart(data: any[], markers: any[]): void {
    const canvas = document.getElementById('processingChart') as HTMLCanvasElement;
    if (!canvas) return;

    const { intercars, allegro, inpost } = this.groupByProvider(data);

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [
          {
            label: 'InterCars',
            data: intercars.map((d: any) => ({ x: d.bucket, y: d.count })),
            borderColor: '#2196f3',
            backgroundColor: 'rgba(33,150,243,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: 'Allegro',
            data: allegro.map((d: any) => ({ x: d.bucket, y: d.count })),
            borderColor: '#ff9800',
            backgroundColor: 'rgba(255,152,0,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: 'InPost',
            data: inpost.map((d: any) => ({ x: d.bucket, y: d.count })),
            borderColor: '#4caf50',
            backgroundColor: 'rgba(76,175,80,0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Liczba uruchomien' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildSuccessFailChart(data: any[], markers: any[]): void {
    const canvas = document.getElementById('successFailChart') as HTMLCanvasElement;
    if (!canvas) return;

    const allBuckets = this.getAllBuckets(data);

    const chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: allBuckets.map((b) => b.bucket),
        datasets: [
          {
            label: 'Sukces',
            data: allBuckets.map((b) => b.completedRuns),
            backgroundColor: '#4caf50',
          },
          {
            label: 'Blad',
            data: allBuckets.map((b) => b.failedRuns),
            backgroundColor: '#f44336',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            stacked: true,
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: { display: true, text: 'Liczba' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private buildDurationChart(data: any[], markers: any[]): void {
    const canvas = document.getElementById('durationChart') as HTMLCanvasElement;
    if (!canvas) return;

    const { intercars, allegro, inpost } = this.groupByProvider(data);

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [
          {
            label: 'InterCars',
            data: intercars.map((d: any) => ({
              x: d.bucket,
              y: d.avgDurationMs ? Math.round(d.avgDurationMs / 1000) : 0,
            })),
            borderColor: '#2196f3',
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: 'Allegro',
            data: allegro.map((d: any) => ({
              x: d.bucket,
              y: d.avgDurationMs ? Math.round(d.avgDurationMs / 1000) : 0,
            })),
            borderColor: '#ff9800',
            tension: 0.3,
            pointRadius: 2,
          },
          {
            label: 'InPost',
            data: inpost.map((d: any) => ({
              x: d.bucket,
              y: d.avgDurationMs ? Math.round(d.avgDurationMs / 1000) : 0,
            })),
            borderColor: '#4caf50',
            tension: 0.3,
            pointRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: { ...this.getTimeConfig(), tooltipFormat: 'YYYY-MM-DD HH:mm' },
            title: { display: true, text: 'Czas' },
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Sredni czas (s)' },
          },
        },
        plugins: {
          markerLines: { markers },
        } as any,
      },
    });
    this.charts.push(chart);
  }

  private groupByProvider(data: any[]): { intercars: any[]; allegro: any[]; inpost: any[] } {
    const intercars: any[] = [];
    const allegro: any[] = [];
    const inpost: any[] = [];

    for (const item of data) {
      const bucket = item._id.bucket;
      const provider = item._id.provider;
      const entry = { bucket, ...item };

      if (provider === 'intercars') intercars.push(entry);
      else if (provider === 'allegro') allegro.push(entry);
      else if (provider === 'inpost') inpost.push(entry);
    }

    return { intercars, allegro, inpost };
  }

  private getAllBuckets(data: any[]): any[] {
    const bucketMap = new Map<string, any>();

    for (const item of data) {
      const key = item._id.bucket;
      if (!bucketMap.has(key)) {
        bucketMap.set(key, {
          bucket: key,
          completedRuns: 0,
          failedRuns: 0,
        });
      }
      const entry = bucketMap.get(key);
      entry.completedRuns += item.completedRuns || 0;
      entry.failedRuns += item.failedRuns || 0;
    }

    return Array.from(bucketMap.values()).sort(
      (a, b) => new Date(a.bucket).getTime() - new Date(b.bucket).getTime(),
    );
  }

  private getTimeConfig(): { unit: TimeUnit; stepSize?: number } {
    switch (this.selectedRange) {
      case '1h':
        return { unit: 'minute', stepSize: 10 };
      case '6h':
        return { unit: 'minute', stepSize: 30 };
      case '24h':
        return { unit: 'hour', stepSize: 1 };
      case '7d':
        return { unit: 'day', stepSize: 1 };
      case '30d':
        return { unit: 'day', stepSize: 1 };
      default:
        return { unit: 'hour', stepSize: 1 };
    }
  }
}
